import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Badge, Modal, ListGroup } from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getFieldMappings, 
  saveFieldMappings, 
  getHubspotContactProperties, 
  getOpportunityFields,
  testFieldMappings
} from '../api/fieldMappings';
import { getSettings, updateSettings } from '../api/settings';
import FieldMappingsSideNav from '../components/FieldMappingsSideNav';
import '../styles/FieldMappings.css';

const FieldMappings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [opportunityFields, setOpportunityFields] = useState([]);
  const [hubspotProperties, setHubspotProperties] = useState([]);
  const [hubspotOptions, setHubspotOptions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [secondaryMappings, setSecondaryMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTestResults, setShowTestResults] = useState(false);
  const [testResults, setTestResults] = useState({ success: [], errors: [] });
  const [activeSection, setActiveSection] = useState(0);
  const [secondaryContactHandling, setSecondaryContactHandling] = useState('manual');
  const [secondaryContactProperty, setSecondaryContactProperty] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Check if user is super_admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch opportunity fields
        const fields = await getOpportunityFields();
        setOpportunityFields(fields);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(fields.map(field => field.category))];
        // Add a "Settings" category for application settings
        uniqueCategories.push('Settings');
        setCategories(uniqueCategories);
        
        // Fetch HubSpot properties
        const properties = await getHubspotContactProperties();
        setHubspotProperties(properties);
        
        // Convert HubSpot properties to react-select options
        const options = properties.map(property => ({
          value: property.name,
          label: `${property.label} (${property.name})`,
          property: property
        }));
        setHubspotOptions(options);
        
        // Fetch existing mappings
        const existingMappings = await getFieldMappings();
        
        // Create primary mapping objects
        const primaryMappingObjects = fields.map(field => {
          const existingMapping = existingMappings.find(m => m.opportunityField === field.name);
          return {
            opportunityField: field.name,
            hubspotField: existingMapping ? existingMapping.hubspotField : ''
          };
        });
        
        setMappings(primaryMappingObjects);
        
        // Create secondary contact mapping objects
        const secondaryMappingObjects = fields.filter(field => 
          field.category === 'Primary Contact' || field.category === 'Contact Address'
        ).map(field => {
          // Create the secondary field name (e.g., Contact2FirstName)
          const secondaryFieldName = `Contact2${field.name.replace('Contact', '')}`;
          // Find if there's an existing mapping for this secondary field
          const existingMapping = existingMappings.find(m => m.opportunityField === secondaryFieldName);
          
          return {
            opportunityField: secondaryFieldName,
            originalField: field.name, // Keep track of the original field for reference
            hubspotField: existingMapping ? existingMapping.hubspotField : ''
          };
        });
        
        setSecondaryMappings(secondaryMappingObjects);
        
        // Fetch settings
        try {
          const settingsData = await getSettings();
          setSecondaryContactHandling(settingsData.secondaryContactHandling || 'manual');
          setSecondaryContactProperty(settingsData.secondaryContactProperty || '');
        } catch (settingsError) {
          console.error('Error fetching settings:', settingsError);
          // Default to manual if settings can't be fetched
          setSecondaryContactHandling('manual');
          setSecondaryContactProperty('');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load field mappings data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Handle mapping change
  const handleMappingChange = (opportunityField, selectedOption) => {
    // Check if this is a secondary contact field
    if (opportunityField.startsWith('Contact2')) {
      setSecondaryMappings(prevMappings => 
        prevMappings.map(mapping => 
          mapping.opportunityField === opportunityField 
            ? { ...mapping, hubspotField: selectedOption ? selectedOption.value : '' } 
            : mapping
        )
      );
    } else {
      setMappings(prevMappings => 
        prevMappings.map(mapping => 
          mapping.opportunityField === opportunityField 
            ? { ...mapping, hubspotField: selectedOption ? selectedOption.value : '' } 
            : mapping
        )
      );
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Filter out mappings with empty hubspotField
      const validPrimaryMappings = mappings.filter(mapping => mapping.hubspotField);
      
      // Add secondary mappings if using HubSpot mode
      let allMappings = [...validPrimaryMappings];
      
      if (secondaryContactHandling === 'hubspot') {
        const validSecondaryMappings = secondaryMappings.filter(mapping => mapping.hubspotField);
        allMappings = [...validPrimaryMappings, ...validSecondaryMappings];
      }
      
      // Save mappings
      await saveFieldMappings(allMappings);
      
      setSuccess('Field mappings saved successfully!');
    } catch (error) {
      console.error('Error saving mappings:', error);
      setError('Failed to save field mappings. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  // Handle settings submission
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    try {
      setSavingSettings(true);
      setError(null);
      setSuccess(null);
      
      // Make sure secondaryContactProperty is set if hubspot mode is selected
      if (secondaryContactHandling === 'hubspot' && !secondaryContactProperty) {
        setError('Please select a HubSpot property to check for secondary contacts.');
        setSavingSettings(false);
        return;
      }
      
      // Save settings
      await updateSettings({ 
        secondaryContactHandling,
        secondaryContactProperty: secondaryContactHandling === 'hubspot' ? secondaryContactProperty : ''
      });
      
      setSuccess('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings. Please try again later.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle test mappings
  const handleTestMappings = async () => {
    try {
      setTesting(true);
      setError(null);
      
      // Filter out mappings with empty hubspotField
      const validPrimaryMappings = mappings.filter(mapping => mapping.hubspotField);
      
      // Add secondary mappings if using HubSpot mode
      let allMappings = [...validPrimaryMappings];
      
      if (secondaryContactHandling === 'hubspot') {
        const validSecondaryMappings = secondaryMappings.filter(mapping => mapping.hubspotField);
        allMappings = [...validPrimaryMappings, ...validSecondaryMappings];
      }
      
      if (allMappings.length === 0) {
        setError('No mappings to test. Please create at least one mapping first.');
        setTesting(false);
        return;
      }
      
      // Test mappings
      const results = await testFieldMappings(allMappings);
      setTestResults(results);
      setShowTestResults(true);
    } catch (error) {
      console.error('Error testing mappings:', error);
      setError('Failed to test field mappings. Please try again later.');
    } finally {
      setTesting(false);
    }
  };

  // Check compatibility between opportunity field and hubspot property
  const checkCompatibility = (opportunityField, hubspotProperty) => {
    if (!opportunityField || !hubspotProperty) return { compatible: true, reason: '' };
    
    const fieldType = opportunityField.type.toLowerCase();
    const propertyType = hubspotProperty.type.toLowerCase();
    
    // Basic compatibility rules
    if (fieldType === 'string' && ['string', 'text', 'textarea'].includes(propertyType)) {
      return { compatible: true, reason: '' };
    }
    
    if (fieldType === 'number' && ['number', 'integer'].includes(propertyType)) {
      return { compatible: true, reason: '' };
    }
    
    if (fieldType === 'date' && ['date', 'datetime'].includes(propertyType)) {
      return { compatible: true, reason: '' };
    }
    
    if (fieldType === 'boolean' && propertyType === 'boolean') {
      return { compatible: true, reason: '' };
    }
    
    // Special case for enumeration/select fields
    if (propertyType === 'enumeration' || propertyType.includes('select')) {
      return { 
        compatible: false, 
        reason: 'Select field requires exact match from options',
        warning: true
      };
    }
    
    // Default case - might work but with warning
    // Display "Text" in UI but keep internal logic using "string"
    const displayFieldType = fieldType === 'string' ? 'Text' : fieldType;
    const displayPropertyType = propertyType === 'string' ? 'Text' : propertyType;
    
    return { 
      compatible: true, 
      reason: `Type mismatch: ${displayFieldType} → ${displayPropertyType}`,
      warning: true
    };
  };

  // Calculate mapping stats for sidebar
  const calculateMappingStats = () => {
    const stats = {};
    
    // First, calculate the secondary mapping stats if needed
    let totalSecondaryFields = 0;
    let mappedSecondaryFields = 0;
    const contactCategories = ['Primary Contact', 'Contact Address'];
    
    if (secondaryContactHandling === 'hubspot') {
      const relevantPrimaryFields = opportunityFields.filter(field => 
        contactCategories.includes(field.category)
      );
      totalSecondaryFields = relevantPrimaryFields.length;
      
      mappedSecondaryFields = secondaryMappings.filter(mapping => {
        // Find the original primary field based on the secondary mapping
        const originalField = relevantPrimaryFields.find(field => 
          field.name === mapping.originalField
        );
        // Check if the original field exists and the secondary field is mapped
        return originalField && mapping.hubspotField;
      }).length;
    }
    
    // Now iterate through categories to set their individual stats
    categories.forEach(category => {
      if (category === 'Settings') {
        // Settings count depends on the mode
        if (secondaryContactHandling === 'hubspot') {
          stats[category] = {
            mapped: mappedSecondaryFields,
            total: totalSecondaryFields
          };
        } else {
          // Manual mode - just show 1/1 to indicate the setting itself
          stats[category] = {
            mapped: 1,
            total: 1
          };
        }
        return; // Move to the next category
      }
      
      // For regular categories (non-Settings)
      const categoryFields = opportunityFields.filter(field => field.category === category);
      const totalPrimaryFields = categoryFields.length;
      
      // Count ONLY primary field mappings for this category
      const mappedPrimaryFieldsCount = categoryFields.filter(field => {
        const mapping = mappings.find(m => m.opportunityField === field.name);
        return mapping && mapping.hubspotField;
      }).length;
      
      stats[category] = {
        mapped: mappedPrimaryFieldsCount, // Only count mapped primary fields here
        total: totalPrimaryFields        // Total is always primary fields for these categories
      };
    });
    
    return stats;
  };

  // Filter HubSpot properties based on search term
  const filteredProperties = searchTerm
    ? hubspotProperties.filter(property => 
        property.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
        property.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : hubspotProperties;

  // Custom styles for React-Select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  // Render settings section
  const renderSettingsSection = () => {
    return (
      <Card>
        <Card.Header className="bg-light">
          <h5 className="mb-0">Application Settings</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSaveSettings}>
            <Form.Group className="mb-4" controlId="secondaryContactHandling">
              <Form.Label className="fw-bold">Secondary Contact Handling</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                Choose how secondary contacts should be handled in the application
              </Form.Text>
              
              <Form.Select 
                value={secondaryContactHandling}
                onChange={(e) => setSecondaryContactHandling(e.target.value)}
                className="mb-2"
              >
                <option value="manual">Manual Entry - Users will add/fill out secondary contacts manually</option>
                <option value="hubspot">From HubSpot - Pull secondary contact information from HubSpot</option>
              </Form.Select>
              
              {secondaryContactHandling === 'hubspot' && (
                <div className="mt-3 mb-3">
                  <Form.Label className="fw-bold">HubSpot Property for Secondary Contact</Form.Label>
                  <Form.Text className="text-muted d-block mb-2">
                    Select the HubSpot property that indicates a secondary contact relationship
                  </Form.Text>
                  
                  <Select
                    value={
                      secondaryContactProperty 
                        ? hubspotOptions.find(option => option.value === secondaryContactProperty) 
                        : null
                    }
                    onChange={(option) => setSecondaryContactProperty(option ? option.value : '')}
                    options={hubspotOptions}
                    isClearable
                    placeholder="Search or select a property..."
                    styles={customStyles}
                    className="basic-single"
                    classNamePrefix="select"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                  />
                  
                  <Form.Text className="text-muted mt-2">
                    This property will be checked to determine if a contact has an associated secondary contact
                  </Form.Text>
                </div>
              )}
              
              <div className="text-muted small">
                {secondaryContactHandling === 'manual' ? (
                  <>Users will need to manually add and fill out secondary contact information when needed.</>
                ) : (
                  <>The system will automatically look for and pull secondary contact information from HubSpot when available.</>
                )}
              </div>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={savingSettings}
              >
                {savingSettings ? (
                  <>
                    <Spinner 
                      as="span" 
                      animation="border" 
                      size="sm" 
                      role="status" 
                      aria-hidden="true" 
                      className="me-1"
                    />
                    Saving...
                  </>
                ) : "Save Settings"}
              </Button>
            </div>
          </Form>
          
          {secondaryContactHandling === 'hubspot' && (
            <div className="mt-5">
              <h5 className="mb-3">Secondary Contact Field Mappings</h5>
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                Configure how secondary contact fields are mapped when pulled from HubSpot. 
                These mappings will be used when a secondary contact is found using the property above.
              </div>
              
              <Form onSubmit={handleSubmit} className="mt-4">
                <Card>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Secondary Contact Fields</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3 fw-bold">
                      <Col md={5}>Form Field</Col>
                      <Col md={7}>HubSpot Contact Property</Col>
                    </Row>
                    
                    {secondaryMappings.map((mapping) => {
                      // Find the original field to get its label and type
                      const originalField = opportunityFields.find(f => f.name === mapping.originalField);
                      if (!originalField) return null;
                      
                      const selectedOption = mapping.hubspotField 
                        ? hubspotOptions.find(option => option.value === mapping.hubspotField) 
                        : null;
                      
                      const selectedProperty = selectedOption 
                        ? hubspotProperties.find(p => p.name === selectedOption.value) 
                        : null;
                      
                      const compatibility = selectedProperty 
                        ? checkCompatibility(originalField, selectedProperty) 
                        : { compatible: true, reason: '' };
                      
                      // Create a display name by replacing "Contact" with "Secondary Contact"
                      const displayLabel = originalField.label;
                      
                      return (
                        <Row key={mapping.opportunityField} className="mb-4 align-items-center mapping-row">
                          <Col md={5}>
                            <div>
                              <strong>Secondary {displayLabel}</strong>
                              <div className="text-muted small">
                                Type: <Badge bg="secondary">
                                  {originalField.type.toLowerCase() === 'string' ? 'Text' : originalField.type}
                                </Badge>
                              </div>
                            </div>
                          </Col>
                          <Col md={7} className="select-container">
                            <Select
                              value={selectedOption}
                              onChange={(option) => handleMappingChange(mapping.opportunityField, option)}
                              options={hubspotOptions}
                              isClearable
                              placeholder="Search or select a property..."
                              styles={customStyles}
                              className="basic-single"
                              classNamePrefix="select"
                              menuPosition="fixed"
                              menuPortalTarget={document.body}
                            />
                            
                            {selectedProperty && (
                              <div className="mt-2">
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg={compatibility.compatible ? (compatibility.warning ? "warning" : "success") : "danger"}>
                                    {selectedProperty.type.toLowerCase() === 'string' ? 'Text' : selectedProperty.type}
                                  </Badge>
                                  
                                  {compatibility.reason && (
                                    <span className={`small ${compatibility.warning ? 'text-warning' : (compatibility.compatible ? 'text-success' : 'text-danger')}`}>
                                      {compatibility.reason}
                                    </span>
                                  )}
                                </div>
                                
                                {selectedProperty.options && selectedProperty.options.length > 0 && (
                                  <div className="mt-1 small text-muted">
                                    Options: {selectedProperty.options.map(opt => opt.label || opt.value).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </Card.Body>
                </Card>
                
                <div className="d-flex justify-content-end mt-3">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner 
                          as="span" 
                          animation="border" 
                          size="sm" 
                          role="status" 
                          aria-hidden="true" 
                          className="me-1"
                        />
                        Saving All Mappings...
                      </>
                    ) : "Save Mappings"}
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading field mappings...</p>
      </Container>
    );
  }

  // Get current category fields
  const currentCategory = categories[activeSection];
  const currentCategoryFields = currentCategory === 'Settings' 
    ? [] 
    : opportunityFields.filter(field => field.category === currentCategory);

  return (
    <div className="page-container">
      <div className="field-mappings-container">
        {/* Sidebar */}
        <div className="field-mappings-sidebar">
          <FieldMappingsSideNav
            categories={categories}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            mappingStats={calculateMappingStats()}
          />
        </div>
        
        {/* Main Content */}
        <div className="field-mappings-content">
          <h3 className="mb-4">Field Mappings Configuration</h3>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="mb-4">
              {success}
            </Alert>
          )}
          
          {/* Render Settings section if selected */}
          {currentCategory === 'Settings' ? (
            renderSettingsSection()
          ) : (
            <Form onSubmit={handleSubmit}>
              <div className="field-mapping-section">
                <Card>
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">{currentCategory}</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3 fw-bold">
                      <Col md={5}>Form Field</Col>
                      <Col md={7}>HubSpot Contact Property</Col>
                    </Row>
                    
                    {currentCategoryFields.map((field) => {
                      const mapping = mappings.find(m => m.opportunityField === field.name);
                      const selectedOption = mapping && mapping.hubspotField 
                        ? hubspotOptions.find(option => option.value === mapping.hubspotField) 
                        : null;
                      
                      const selectedProperty = selectedOption 
                        ? hubspotProperties.find(p => p.name === selectedOption.value) 
                        : null;
                      
                      const compatibility = selectedProperty 
                        ? checkCompatibility(field, selectedProperty) 
                        : { compatible: true, reason: '' };
                      
                      return (
                        <Row key={field.name} className="mb-4 align-items-center mapping-row">
                          <Col md={5}>
                            <div>
                              <strong>{field.label}</strong>
                              {/* <div className="text-muted small">{field.name}</div> */}
                              <div className="text-muted small">
                                Type: <Badge bg="secondary">
                                  {field.type.toLowerCase() === 'string' ? 'Text' : field.type}
                                </Badge>
                              </div>
                            </div>
                          </Col>
                          <Col md={7} className="select-container">
                            <Select
                              value={selectedOption}
                              onChange={(option) => handleMappingChange(field.name, option)}
                              options={hubspotOptions}
                              isClearable
                              placeholder="Search or select a property..."
                              styles={customStyles}
                              className="basic-single"
                              classNamePrefix="select"
                              menuPosition="fixed"
                              menuPortalTarget={document.body}
                            />
                            
                            {selectedProperty && (
                              <div className="mt-2">
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg={compatibility.compatible ? (compatibility.warning ? "warning" : "success") : "danger"}>
                                    {selectedProperty.type.toLowerCase() === 'string' ? 'Text' : selectedProperty.type}
                                  </Badge>
                                  
                                  {compatibility.reason && (
                                    <span className={`small ${compatibility.warning ? 'text-warning' : (compatibility.compatible ? 'text-success' : 'text-danger')}`}>
                                      {compatibility.reason}
                                    </span>
                                  )}
                                </div>
                                
                                {selectedProperty.options && selectedProperty.options.length > 0 && (
                                  <div className="mt-1 small text-muted">
                                    Options: {selectedProperty.options.map(opt => opt.label || opt.value).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </Card.Body>
                </Card>
              </div>
              
              <div className="mapping-footer">
                <div>
                  <Alert variant="info" className="d-inline-block mb-0">
                    <strong>{mappings.filter(m => m.hubspotField).length}</strong> of {opportunityFields.length} fields mapped
                  </Alert>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    type="button" 
                    variant="outline-primary"
                    size="lg"
                    disabled={testing}
                    className="d-flex align-items-center gap-2"
                    onClick={handleTestMappings}
                  >
                    {testing && <Spinner animation="border" size="sm" />}
                    {testing ? 'Testing...' : 'Test Mappings'}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={saving}
                    className="d-flex align-items-center gap-2"
                  >
                    {saving && <Spinner animation="border" size="sm" />}
                    {saving ? 'Saving...' : 'Save Mappings'}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </div>
      </div>
      
      {/* Test Results Modal */}
      <Modal
        show={showTestResults}
        onHide={() => setShowTestResults(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Field Mapping Test Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testResults.errors.length > 0 && (
            <>
              <h5 className="text-danger">Errors ({testResults.errors.length})</h5>
              <ListGroup variant="flush" className="mb-4">
                {testResults.errors.map((error, index) => (
                  <ListGroup.Item key={index} className="border-0 border-bottom">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{error.field}</strong> → {error.property}
                      </div>
                      <Badge bg="danger">Error</Badge>
                    </div>
                    <div className="text-danger small mt-1">{error.message}</div>
                    {error.details && (
                      <div className="small text-muted mt-1">
                        {error.details}
                      </div>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          
          {testResults.success.length > 0 && (
            <>
              <h5 className="text-success">Successful ({testResults.success.length})</h5>
              <ListGroup variant="flush">
                {testResults.success.map((success, index) => (
                  <ListGroup.Item key={index} className="border-0 border-bottom">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{success.field}</strong> → {success.property}
                      </div>
                      <Badge bg="success">Success</Badge>
                    </div>
                    <div className="small text-muted mt-1">
                      Test value: {success.testValue}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestResults(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FieldMappings; 